import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { useAuthOptional } from "@/contexts/AuthContext";
import { getMyAccountantProfile } from "@/services/authService";

const BASE_TITLE = "Fiscalo";

/**
 * Sets document.title.
 *
 *   - Accountant on /accountant* routes: "{studioName} | Fiscalo" when the
 *     accountant runs a studio, otherwise just "Fiscalo" (solo contadores
 *     don't get a personal-name prefix).
 *   - Anywhere else with an active tenant: "{tenantName} | Fiscalo".
 *   - Logged-out / no context: "Fiscalo".
 *
 * Uses useAuthOptional so it does not throw when context is temporarily
 * unavailable (e.g. during HMR).
 */
export function PortalDocumentTitle(): null {
  const auth = useAuthOptional();
  const location = useLocation();
  const user = auth?.user ?? null;
  const tenantName = user?.tenantName;

  const isAccountant =
    user?.accountType === "accountant" || user?.role === "accountant";
  const onAccountantHome = location.pathname.startsWith("/accountant");

  // Reuse the StudioContextBar query key so the cache is shared and we don't
  // double-fetch the accountant profile.
  const { data: accountantProfile } = useQuery({
    queryKey: ["accountant", user?.id ?? null, "profile"],
    queryFn: getMyAccountantProfile,
    enabled: isAccountant && onAccountantHome && Boolean(user?.id),
  });

  const studioName =
    accountantProfile?.profile?.kind === "studio"
      ? (accountantProfile.profile.studioName?.trim() ?? null)
      : null;

  useEffect(() => {
    let next = BASE_TITLE;
    if (isAccountant && onAccountantHome) {
      next = studioName ? `${studioName} | ${BASE_TITLE}` : BASE_TITLE;
    } else if (tenantName) {
      next = `${tenantName} | ${BASE_TITLE}`;
    }
    document.title = next;
  }, [isAccountant, onAccountantHome, studioName, tenantName]);

  return null;
}
